'use client';

import Link from 'next/link';
import styles from './MvpLandingPage.module.scss';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock,
  Database,
  Eye,
  Layers,
  Search,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Locale } from '@/i18n-config';
import { BusinessModelCta } from '@/src/components/hestiaDesign/BusinessModelCta/BusinessModelCta';
import { ProcessTimeline } from '@/src/components/SliceMachine/ProcessTimeline/ProcessTimeline';
import { buildNorthStarHref } from '@/src/data/mvpPages';
import { gaCta } from '@/src/helpers/gtag';
import { localizeHref } from '@/src/helpers/localizeLinks';
import { useAOS } from '@/src/lib/hooks/useAOS';
import type { MvpPage } from '@/src/types/MvpPage';

const painIcons = [AlertTriangle, Clock, Database];
const featureIcons = [Zap, Layers, Shield];
const metricIcons = [TrendingUp, BarChart3, CheckCircle2];

export function MvpLandingPage({ entry, lang }: { entry: MvpPage; lang: Locale }) {
  useAOS();

  const t = <T,>(localized: Record<Locale, T>) => localized[lang];
  const primaryHref = localizeHref('/rendez-vous', lang);
  const secondaryHref = buildNorthStarHref(entry, lang);

  const problemData = {
    eyebrow: t(entry.problem.eyebrow),
    title: t(entry.problem.title),
    description: t(entry.problem.subtitle),
    pains: entry.problem.pains,
  };

  const conceptData = {
    eyebrow: t(entry.concept.eyebrow),
    title: t(entry.concept.title),
    description: t(entry.concept.description),
    features: entry.concept.features,
  };

  const impactData = {
    eyebrow: t(entry.impact.eyebrow),
    title: t(entry.impact.title),
    subtitle: t(entry.impact.subtitle),
    metrics: entry.impact.metrics,
  };

  const deliveryTimeline = {
    badge: t(entry.delivery.eyebrow),
    title: t(entry.delivery.title),
    subtitle: t(entry.delivery.subtitle),
    steps: entry.delivery.steps.map((step) => ({
      title: t(step.title),
      description: t(step.description),
      duration: step.duration ? t(step.duration) : undefined,
    })),
  };

  return (
    <main className={styles.root}>
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>{t(entry.hero.eyebrow)}</span>
          <h1 className={styles.heroTitle}>
            {t(entry.hero.title)} <em>{t(entry.hero.highlightedTitle)}</em>
          </h1>
          <p className={styles.heroDescription}>{t(entry.hero.description)}</p>
          <div className={styles.heroActions}>
            <Link
              href={primaryHref}
              className={styles.btnPrimary}
              onClick={() => gaCta(t(entry.hero.primaryCta), 'mvp_hero_primary', primaryHref)}
            >
              {t(entry.hero.primaryCta)}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link
              href={secondaryHref}
              className={styles.btnSecondary}
              onClick={() => gaCta(t(entry.hero.secondaryCta), 'mvp_hero_secondary', secondaryHref)}
            >
              {t(entry.hero.secondaryCta)}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Problem ── */}
      <section className={styles.section}>
        <div className={styles.container}>
          <header className={styles.sectionHeader} data-aos="fade-up" data-aos-duration="700">
            <span className={styles.sectionEyebrow}>{problemData.eyebrow}</span>
            <h2 className={styles.sectionTitle}>{problemData.title}</h2>
            <p className={styles.sectionSubtitle}>{problemData.description}</p>
          </header>

          <div className={styles.problemGrid}>
            {problemData.pains.map((pain, index) => {
              const Icon = painIcons[index % painIcons.length];
              return (
                <article
                  key={pain.title.fr}
                  className={styles.problemCard}
                  data-aos="fade-up"
                  data-aos-duration="700"
                  data-aos-delay={index * 100}
                >
                  <div className={styles.problemIcon}>
                    <Icon size={28} strokeWidth={1.6} aria-hidden="true" />
                  </div>
                  <h3 className={styles.cardTitle}>{t(pain.title)}</h3>
                  <p className={styles.cardText}>{t(pain.description)}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Concept / Features ── */}
      <section className={`${styles.section} ${styles.sectionTint}`}>
        <div className={styles.container}>
          <header className={styles.sectionHeader} data-aos="fade-up" data-aos-duration="700">
            <span className={styles.sectionEyebrow}>{conceptData.eyebrow}</span>
            <h2 className={styles.sectionTitle}>{conceptData.title}</h2>
            <p className={styles.sectionSubtitle}>{conceptData.description}</p>
          </header>

          <div className={styles.featureGrid}>
            {conceptData.features.map((feature, index) => {
              const Icon = featureIcons[index % featureIcons.length];
              return (
                <article
                  key={feature.title.fr}
                  className={styles.featureCard}
                  data-aos="fade-up"
                  data-aos-duration="700"
                  data-aos-delay={index * 100}
                >
                  <div className={styles.featureIcon}>
                    <Icon size={32} strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <h3 className={styles.cardTitle}>{t(feature.title)}</h3>
                  <p className={styles.cardText}>{t(feature.description)}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Impact ── */}
      <section className={styles.section}>
        <div className={styles.container}>
          <header className={styles.sectionHeader} data-aos="fade-up" data-aos-duration="700">
            <span className={styles.sectionEyebrow}>{impactData.eyebrow}</span>
            <h2 className={styles.sectionTitle}>{impactData.title}</h2>
            <p className={styles.sectionSubtitle}>{impactData.subtitle}</p>
          </header>

          <div className={styles.impactGrid}>
            {impactData.metrics.map((metric, index) => {
              const Icon = metricIcons[index % metricIcons.length];
              return (
                <article
                  key={metric.label.fr}
                  className={styles.impactCard}
                  data-aos="fade-up"
                  data-aos-duration="700"
                  data-aos-delay={index * 100}
                >
                  <div className={styles.impactIcon}>
                    <Icon size={24} strokeWidth={1.8} aria-hidden="true" />
                  </div>
                  <span className={styles.impactValue}>{t(metric.value)}</span>
                  <span className={styles.impactLabel}>{t(metric.label)}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Delivery Timeline ── */}
      <section className={`${styles.section} ${styles.sectionTint}`}>
        <div className={styles.container}>
          <ProcessTimeline data={deliveryTimeline} className={styles.timeline} />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <BusinessModelCta
        title={t(entry.cta.title)}
        highlightedTitle={t(entry.cta.highlightedTitle)}
        description={t(entry.cta.description)}
        primaryCta={t(entry.cta.primaryCta)}
        primaryHref={primaryHref}
        secondaryCta={t(entry.cta.secondaryCta)}
        secondaryHref={secondaryHref}
        tilt="left"
      />
    </main>
  );
}
